import React, { useState, useEffect } from 'react';
import { companyService } from '../../services/api';
import Swal from 'sweetalert2';

const CompanyProfile = ({ user, onProfileUpdate }) => {
    const isAdmin = user?.role === 'Administrator';
    const [company, setCompany] = useState({
        name: '',
        ntn: '',
        address: '',
        email: '',
        phone: '',
        fax: '',
        website: '',
        logo: '',
        stamp: '',
        distributorLogos: '[]' // JSON string in DB, but we parse it on load
    });
    const [logosList, setLogosList] = useState([]); // Parsed logo objects
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchCompanyProfile();
    }, []);

    const fetchCompanyProfile = async () => {
        try {
            const response = await companyService.getProfile();
            const data = response.data;
            setCompany(data);

            // Parse distributorLogos
            try {
                if (data.distributorLogos) {
                    const parsed = JSON.parse(data.distributorLogos);
                    setLogosList(Array.isArray(parsed) ? parsed : []);
                } else {
                    setLogosList([]);
                }
            } catch (e) {
                console.error("Failed to parse logos", e);
                setLogosList([]);
            }

            setLoading(false);
        } catch (error) {
            console.error('Error fetching company profile:', error);
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setCompany({ ...company, [e.target.name]: e.target.value });
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setCompany({ ...company, logo: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleStampUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setCompany({ ...company, stamp: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAddDistLogo = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const newLogo = {
                    id: Date.now(), // Simple ID
                    src: reader.result,
                    name: '', // Initialize name
                    active: true // Default active
                };
                setLogosList(prev => [...prev, newLogo]);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleToggleActive = (id) => {
        setLogosList(prev => prev.map(logo =>
            logo.id === id ? { ...logo, active: !logo.active } : logo
        ));
    };

    const handleLogoNameChange = (id, newName) => {
        setLogosList(prev => prev.map(logo =>
            logo.id === id ? { ...logo, name: newName } : logo
        ));
    };

    const handleDeleteLogo = (id) => {
        Swal.fire({
            title: 'Delete Logo?',
            text: "Remove this distributor logo?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Delete'
        }).then((result) => {
            if (result.isConfirmed) {
                setLogosList(prev => prev.filter(logo => logo.id !== id));
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Update company object with the latest list as JSON string
            const updatedCompany = {
                ...company,
                distributorLogos: JSON.stringify(logosList)
            };

            await companyService.updateProfile(updatedCompany);
            if (onProfileUpdate) onProfileUpdate(); // Refresh global profile state
            Swal.fire('Success', 'Company profile updated successfully!', 'success');
        } catch (error) {
            console.error('Error updating profile:', error);
            Swal.fire('Error', 'Failed to update profile', 'error');
        }
    };

    if (loading) return <div className="text-center mt-5">Loading...</div>;

    return (
        <div className="container mt-4">
            <div className="mb-4">
                <h2 className="mb-1" style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    fontWeight: '700',
                    fontSize: '2rem'
                }}>
                    Company Profile
                </h2>
                <p className="text-muted mb-0">Manage your company details, logo and branding</p>
            </div>
            <div className="card shadow-sm">
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-md-4 text-center mb-3">
                                <div className="mb-3">
                                    <h6 className="text-muted">Main Logo</h6>
                                    {company.logo ? (
                                        <img src={company.logo} alt="Company Logo" className="img-thumbnail" style={{ maxHeight: '150px' }} />
                                    ) : (
                                        <div className="alert alert-secondary">No Logo Uploaded</div>
                                    )}
                                    <div className="mt-2">
                                        <label htmlFor="logo-upload" className={`btn btn-outline-primary btn-sm ${!isAdmin ? 'disabled' : ''}`}>
                                            Change Logo
                                        </label>
                                        {isAdmin && (
                                            <input
                                                type="file"
                                                id="logo-upload"
                                                className="d-none"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="mb-3 border-top pt-3">
                                    <h6 className="text-muted">Company Stamp</h6>
                                    {company.stamp ? (
                                        <img src={company.stamp} alt="Company Stamp" className="img-thumbnail" style={{ maxHeight: '100px' }} />
                                    ) : (
                                        <div className="alert alert-secondary py-2">No Stamp</div>
                                    )}
                                    <div className="mt-2">
                                        <label htmlFor="stamp-upload" className={`btn btn-outline-primary btn-sm ${!isAdmin ? 'disabled' : ''}`}>
                                            Change Stamp
                                        </label>
                                        {isAdmin && (
                                            <input
                                                type="file"
                                                id="stamp-upload"
                                                className="d-none"
                                                accept="image/*"
                                                onChange={handleStampUpload}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-8">
                                <div className="mb-3">
                                    <label className="form-label">Company Name *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name"
                                        value={company.name}
                                        onChange={handleChange}
                                        required
                                        disabled={!isAdmin}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">NTN</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="ntn"
                                        value={company.ntn || ''}
                                        onChange={handleChange}
                                        disabled={!isAdmin}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Address</label>
                                    <textarea
                                        className="form-control"
                                        name="address"
                                        value={company.address || ''}
                                        onChange={handleChange}
                                        rows="3"
                                        disabled={!isAdmin}
                                    />
                                </div>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Email</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            name="email"
                                            value={company.email || ''}
                                            onChange={handleChange}
                                            disabled={!isAdmin}
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Phone</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="phone"
                                            value={company.phone || ''}
                                            onChange={handleChange}
                                            disabled={!isAdmin}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Fax</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="fax"
                                            value={company.fax || ''}
                                            onChange={handleChange}
                                            disabled={!isAdmin}
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Website</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="website"
                                            value={company.website || ''}
                                            onChange={handleChange}
                                            disabled={!isAdmin}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Distributor Logos Section */}
                        <div className="border-top pt-4 mt-2">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">Authorized Distributor Logos</h5>
                                {isAdmin && (
                                    <div>
                                        <label htmlFor="dist-upload-multi" className="btn btn-sm btn-outline-success">
                                            + Add Logo
                                        </label>
                                        <input
                                            type="file"
                                            id="dist-upload-multi"
                                            className="d-none"
                                            accept="image/*"
                                            onChange={handleAddDistLogo}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="row">
                                {logosList.length === 0 ? (
                                    <div className="col-12 text-center text-muted fst-italic py-3">
                                        No authorized distributor logos added.
                                    </div>
                                ) : (
                                    logosList.map((item) => (
                                        <div key={item.id} className="col-6 col-md-3 mb-3">
                                            <div className={`card h-100 ${item.active ? 'border-primary' : 'border-secondary opacity-75'}`}>
                                                <div className="card-body p-2 text-center d-flex flex-column justify-content-between">
                                                    <img
                                                        src={item.src}
                                                        alt="Dist Logo"
                                                        className="img-fluid mb-2"
                                                        style={{ maxHeight: '80px', objectFit: 'contain' }}
                                                    />
                                                    {isAdmin && (
                                                        <div className="mb-2">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm text-center"
                                                                placeholder="e.g. Bosch"
                                                                value={item.name || ''}
                                                                onChange={(e) => handleLogoNameChange(item.id, e.target.value)}
                                                            />
                                                        </div>
                                                    )}
                                                    {isAdmin && (
                                                        <div className="d-flex justify-content-center gap-2">
                                                            <button
                                                                type="button"
                                                                className={`btn btn-sm ${item.active ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                                onClick={() => handleToggleActive(item.id)}
                                                                title={item.active ? "Currently Visible" : "Currently Hidden"}
                                                            >
                                                                {item.active ? 'Active' : 'Hidden'}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => handleDeleteLogo(item.id)}
                                                                title="Remove"
                                                            >
                                                                <i className="bi bi-trash"></i> X
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        <div className="text-end mt-4">
                            {isAdmin && (
                                <button type="submit" className="btn btn-lg btn-primary px-5">Save Profile Changes</button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CompanyProfile;
